/* eslint-disable complexity */
import { Rotationsplan } from '@/domain/mitarbeiter/features/rotationsplan/entities/Rotationsplan';
import { Standort } from '@/domain/mitarbeiter/features/rotationsplan/entities/Standort';
import type {
  Abschnitt,
  KalenderFilterOptions,
  Status,
  UebersichtFilterOptions,
} from '@/domain/mitarbeiter/features/rotationsplan/features/shared/types';

function filterByStandorte<T extends { standortId: string }>(
  items: T[],
  standorte: Standort[],
): T[] {
  if (standorte.length < 1) return items;

  const selectedStandortIds = new Set(standorte.map((standort) => standort.id));
  return items.filter((item) => selectedStandortIds.has(item.standortId));
}

function filterByBerufe<T extends { beruf: { name: string } }>(
  items: T[],
  berufe: string[],
): T[] {
  if (berufe.length < 1) return items;
  const names = new Set(berufe);
  return items.filter((item) => names.has(item.beruf.name));
}

function filterByAbschnitte(
  items: Rotationsplan[],
  abschnitte: Abschnitt[],
): Rotationsplan[] {
  if (abschnitte.length < 1) return items;
  const names = new Set(abschnitte.map((a) => a.name));
  return items.filter((item) => names.has(item.getCurrentLehrjahr()));
}

function filterByStatus(
  items: Rotationsplan[],
  status: Status[],
): Rotationsplan[] {
  if (status.length < 1) return items;

  return items.filter((item) => {
    let istGeplant = false;
    let istAktiv = false;
    let istAbgeschlossen = false;

    if (status.includes('geplant')) {
      // Hier prüfe ich nicht auf die Anzahl der nächsten Stationen, da ein geplanter Rotationsplan theoretisch auch ein leerer Plan sein kann
      istGeplant =
        item.getCurrentStation() === undefined &&
        item.getAbgeschlosseneStationen().length === 0;
    }
    if (status.includes('aktiv')) {
      istAktiv = item.getCurrentStation() !== undefined;
    }
    if (status.includes('abgeschlossen')) {
      istAbgeschlossen =
        item.getCurrentStation() === undefined &&
        item.getNextStation() === undefined &&
        item.getAbgeschlosseneStationen().length > 0;
    }

    return istGeplant || istAktiv || istAbgeschlossen;
  });
}

export function filterRotationsplaene(
  type: keyof KalenderFilterOptions | keyof UebersichtFilterOptions,
  filterInput:
    | KalenderFilterOptions[keyof KalenderFilterOptions]
    | UebersichtFilterOptions[keyof UebersichtFilterOptions],
  otherFilters: KalenderFilterOptions | UebersichtFilterOptions,
  rotationsplaene: Rotationsplan[],
): Rotationsplan[] {
  let filtered = [...rotationsplaene];

  if (filterInput.length > 0) {
    switch (type) {
      case 'standorte':
        filtered = filterByStandorte(filtered, filterInput as Standort[]);
        break;
      case 'berufe':
        filtered = filterByBerufe(filtered, filterInput as string[]);
        break;
      case 'abschnitte':
        filtered = filterByAbschnitte(filtered, filterInput as Abschnitt[]);
        break;
      case 'status':
        filtered = filterByStatus(filtered, filterInput as Status[]);
        break;
    }
  }

  if (type !== 'berufe') {
    filtered = filterByBerufe(filtered, otherFilters.berufe);
  }
  if (type !== 'abschnitte') {
    filtered = filterByAbschnitte(filtered, otherFilters.abschnitte);
  }
  if (type !== 'standorte' && 'standorte' in otherFilters) {
    filtered = filterByStandorte(filtered, otherFilters.standorte);
  }
  if (type !== 'status' && 'status' in otherFilters) {
    filtered = filterByStatus(filtered, otherFilters.status);
  }

  return filtered;
}
